// Pinia store for front-end toast notifications.
// These are distinct from the backend notifications displayed in the flyout/overlay.

import '@types'
import { acceptHMRUpdate, defineStore } from 'pinia'
import { computed, markRaw, nextTick, ref, toValue } from 'vue'
import { useOverlaysStore } from './overlays'

let toastNotificationId = 0
let hideRequestId = 0

const defaultOptions = {
  type: 'transient',
  onClick: null,
  link: null,
  icon: null,
}
export function createToastNotification (message, options = {}) {
  const { type, onClick, link, icon } = { ...defaultOptions, ...options }
  const _id = toastNotificationId++
  return {
    get id () { return _id },
    get type () { return type },
    get isTransient () { return this.type === 'transient' },
    get isPersistent () { return this.type === 'persistent' },
    get isSmart () { return this.type === 'smart' },
    get message () { return Array.isArray(message) ? message : [message] },
    get onClick () { return onClick },
    get link () { return link },
    get icon () { return icon ? markRaw(icon) : null },
    get centered () { return options.centered === true },
  }
}

export const useToastNotificationsStore = defineStore('toastNotifications', () => {
  const overlaysStore = useOverlaysStore()

  const toastNotifications = ref([])
  /** @type {Ref<Record<number, ToastNotificationHideRequest>>} */
  const hideRequests = ref({})

  const transientToastNotifications = computed(() => toastNotifications.value.filter(toast => toast.isTransient))
  const persistentToastNotifications = computed(() => toastNotifications.value.filter(toast => toast.isPersistent))
  const smartToastNotifications = computed(() => toastNotifications.value.filter(toast => toast.isSmart))

  const activeHideRequests = computed(() =>
    Object.values(hideRequests.value).filter(({ predicate }) => predicate === null || toValue(predicate) === true)
  )
  const activeHideRequestTypes = computed(() => new Set(activeHideRequests.value.map(({ type }) => type).filter(Boolean)))
  const shouldHideAllToastNotifications = computed(() => activeHideRequests.value.some(({ type }) => type === null))
  const visibleToastNotifications = computed(() =>
    shouldHideAllToastNotifications.value
      ? []
      : toastNotifications.value.filter(toast => !activeHideRequestTypes.value.has(toast.type))
  )

  function getToastNotificationById (id) {
    return toastNotifications.value.find(toast => toast.id === id)
  }
  function showToastNotification (toast) {
    if (toast.isTransient && overlaysStore.isAnyOverlayOpen) {
      // We don't want to show transient toasts when overlays are open.
      return null
    }
    toastNotifications.value.push(toast)
    if (toast.isTransient) {
      nextTick(() => {
        setTimeout(() => dismissToastNotification(toast.id), 6000)
      })
    }
    return toast.id
  }
  // Transient notifications are the default and are automatically dismissed after a short time.
  function showTransientToast (message, options = {}) {
    const toast = createToastNotification(message, options)
    return showToastNotification(toast)
  }
  // Persistent notifications require the user to do something before they're dismissed.
  function showPersistentToast (message, options = {}) {
    const toast = createToastNotification(message, { ...options, type: 'persistent' })
    return showToastNotification(toast)
  }
  // Smart notifications denote when we do something smart on behalf of the user.
  function showSmartToast (message, options = {}) {
    const toast = createToastNotification(message, { ...options, type: 'smart' })
    return showToastNotification(toast)
  }
  function dismissToastNotification (id) {
    const index = toastNotifications.value.findIndex(toast => toast.id === id)
    if (index !== -1) {
      toastNotifications.value.splice(index, 1)
    }
  }
  function clearToastNotifications () {
    toastNotifications.value = []
  }
  /** @param {ToastNotificationHideRequest} hideRequest */
  function addHideRequest(hideRequest = {}) {
    const { predicate = null, type = null } = hideRequest
    const id = hideRequestId++
    hideRequests.value[id] = { predicate, type }
    return id
  }
  /** @param {number} id */
  function removeHideRequest(id) {
    delete hideRequests.value[id]
  }

  return {
    toastNotifications,
    hideRequests,
    transientToastNotifications,
    persistentToastNotifications,
    smartToastNotifications,
    activeHideRequests,
    activeHideRequestTypes,
    shouldHideAllToastNotifications,
    visibleToastNotifications,
    getToastNotificationById,
    showToastNotification,
    showTransientToast,
    showPersistentToast,
    showSmartToast,
    dismissToastNotification,
    clearToastNotifications,
    addHideRequest,
    removeHideRequest,
  }
})

import.meta.hot?.accept(acceptHMRUpdate(useToastNotificationsStore, import.meta.hot))
